/**
 * Editor visual de hotspots de la escena.
 *
 * Activación: tecla E. En modo edición, cada hotspot recibe outline punteado,
 * tiradores de redimensión en esquinas y bordes, y panel flotante con
 * coordenadas en vivo. Las URLs (`href`) de los hotspots se desactivan
 * mientras el editor está activo para evitar navegación accidental.
 *
 * Atajos de teclado en modo edición:
 *   - Click en sprite             → seleccionar
 *   - Arrastrar sprite            → mover
 *   - Arrastrar tiradores         → redimensionar (Shift = libre, sin proporción)
 *   - ←↑→↓                        → mover 1 px (Shift = 10 px)
 *   - Alt + ←↑→↓                  → redimensionar desde borde derecho/inferior
 *   - Ctrl + ←↑→↓                 → redimensionar desde borde izquierdo/superior
 *   - Q / W                       → escalar manteniendo proporción
 *   - C                           → copiar CSS al portapapeles
 *   - R                           → reset al estado inicial
 *   - H                           → mostrar/ocultar panel
 *
 * El editor opera sobre coordenadas en píxeles de "scene space" (1344×768),
 * convertidas desde y a píxeles reales de pantalla. El CSS final se emite
 * en porcentajes para que la escena escale con el viewport sin distorsión.
 */

// ─── Constantes ───────────────────────────────────────────────────────────

/** Dimensiones lógicas de la escena (píxeles internos del PNG de fondo). */
const SCENE_W = 1344;
const SCENE_H = 768;

/** Tamaño mínimo de hotspot (en pixeles de scene space). Evita arrastrar a 0. */
const MIN_SIZE = 20;

// ─── Tipos ────────────────────────────────────────────────────────────────

/** Rectángulo en coordenadas de scene space. */
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Posición de un tirador de redimensión. */
type Corner = 'tl' | 'tr' | 'bl' | 'br' | 't' | 'b' | 'l' | 'r';

/** Estado durante un drag (mover o redimensionar). */
interface DragState {
  type: 'move' | 'resize';
  el: HTMLElement;
  /** Rectángulo del hotspot al iniciar el drag. */
  startRect: Rect;
  /** Posición del cursor al iniciar (en pixeles de pantalla). */
  startMouse: { x: number; y: number };
  /** Sólo para resize: qué tirador se está arrastrando. */
  corner?: Corner;
  /** Sólo para resize: relación width/height al iniciar. */
  ratio?: number;
}

// ─── Estado del editor ────────────────────────────────────────────────────

let editMode = false;
let selected: HTMLElement | null = null;
let initialState: Map<string, string> | null = null;
let dragging: DragState | null = null;
let panel: HTMLElement | null = null;
let docListenersAttached = false;
let initialized = false;

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Devuelve el ID lógico del elemento editable: el `alt` de su sprite, o
 * el `data-name` si no tiene sprite (p.ej. el halo del proyector).
 */
function nameOf(hotspot: HTMLElement): string {
  return hotspot.querySelector('img')?.alt ?? hotspot.dataset.name ?? '';
}

/** Convierte coords del hotspot a scene space (pixeles internos del PNG). */
function getRect(hotspot: HTMLElement): Rect {
  const wrap = document.querySelector<HTMLElement>('.scene-wrapper');
  if (!wrap) return { left: 0, top: 0, width: 0, height: 0 };
  const wrapRect = wrap.getBoundingClientRect();
  const r = hotspot.getBoundingClientRect();
  const scaleX = SCENE_W / wrapRect.width;
  const scaleY = SCENE_H / wrapRect.height;
  return {
    left: (r.left - wrapRect.left) * scaleX,
    top: (r.top - wrapRect.top) * scaleY,
    width: r.width * scaleX,
    height: r.height * scaleY,
  };
}

/** Aplica un Rect al hotspot como porcentajes (formato CSS final). */
function setRect(hotspot: HTMLElement, scene: Rect): void {
  hotspot.style.left = ((100 * scene.left) / SCENE_W).toFixed(4) + '%';
  hotspot.style.top = ((100 * scene.top) / SCENE_H).toFixed(4) + '%';
  hotspot.style.width = ((100 * scene.width) / SCENE_W).toFixed(4) + '%';
  hotspot.style.height = ((100 * scene.height) / SCENE_H).toFixed(4) + '%';
  updatePanel();
}

/** Guarda el cssText inicial de cada hotspot (para el RESET). Idempotente. */
function snapshotInitial(): void {
  if (initialState) return;
  initialState = new Map();
  document.querySelectorAll<HTMLElement>('.hotspot').forEach((h) => {
    initialState!.set(nameOf(h), h.style.cssText);
  });
}

// ─── Panel flotante ───────────────────────────────────────────────────────

function buildPanel(): HTMLElement {
  const el = document.createElement('div');
  el.id = 'edit-panel';
  el.innerHTML = `
    <h3>🎯 EDITOR DE SPRITES</h3>
    <div class="row"><span class="label">Selección:</span>
      <span class="selname" id="ep-name">—</span></div>
    <div class="row"><span class="label">left:</span>
      <span class="val" id="ep-l">—</span></div>
    <div class="row"><span class="label">top:</span>
      <span class="val" id="ep-t">—</span></div>
    <div class="row"><span class="label">width:</span>
      <span class="val" id="ep-w">—</span></div>
    <div class="row"><span class="label">height:</span>
      <span class="val" id="ep-h">—</span></div>
    <div class="row"><span class="label">ratio:</span>
      <span class="val" id="ep-r">—</span></div>
    <div style="margin-top:8px;text-align:center;">
      <button id="ep-copy">📋 COPIAR CSS</button>
      <button id="ep-export">💾 EXPORTAR JSON</button>
      <button id="ep-reset">↺ RESET</button>
    </div>
    <div class="help">
      <kbd>E</kbd> activar/salir &nbsp;<kbd>H</kbd> ocultar panel<br>
      <kbd>click</kbd> seleccionar &nbsp;<kbd>drag</kbd> mover<br><br>
      <strong style="color:#ffc107">MOVER:</strong><br>
      <kbd>↑↓←→</kbd> 1px &nbsp;<kbd>Shift</kbd>+ 10px<br><br>
      <strong style="color:#ffc107">REDIMENSIONAR:</strong><br>
      <kbd>Alt</kbd>+<kbd>→</kbd>/<kbd>←</kbd> ancho +/− (borde dcho)<br>
      <kbd>Alt</kbd>+<kbd>↓</kbd>/<kbd>↑</kbd> alto +/− (borde inf)<br>
      <kbd>Ctrl</kbd>+<kbd>←</kbd>/<kbd>→</kbd> ancho desde izq<br>
      <kbd>Ctrl</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> alto desde sup<br>
      <kbd>Q</kbd>/<kbd>W</kbd> escalar (proporción)<br>
      Tiradores esquinas + <kbd>Shift</kbd> = libre<br>
      Tiradores lados = ancho o alto solo<br><br>
      <kbd>C</kbd> copiar CSS &nbsp;<kbd>R</kbd> reset
    </div>
  `;
  document.body.appendChild(el);
  el.querySelector<HTMLButtonElement>('#ep-copy')!.onclick = copyCSS;
  el.querySelector<HTMLButtonElement>('#ep-export')!.onclick = exportJSON;
  el.querySelector<HTMLButtonElement>('#ep-reset')!.onclick = resetAll;
  return el;
}

function updatePanel(): void {
  if (!panel) return;
  const fields = ['name', 'l', 't', 'w', 'h', 'r'] as const;
  if (!selected) {
    fields.forEach((k) => {
      const e = document.getElementById('ep-' + k);
      if (e) e.textContent = '—';
    });
    return;
  }
  const r = getRect(selected);
  const name = nameOf(selected);
  const setText = (id: string, text: string): void => {
    const e = document.getElementById('ep-' + id);
    if (e) e.textContent = text;
  };
  setText('name', name);
  setText('l', `${r.left.toFixed(0)}px (${((100 * r.left) / SCENE_W).toFixed(2)}%)`);
  setText('t', `${r.top.toFixed(0)}px (${((100 * r.top) / SCENE_H).toFixed(2)}%)`);
  setText('w', `${r.width.toFixed(0)}px`);
  setText('h', `${r.height.toFixed(0)}px`);
  setText('r', (r.width / r.height).toFixed(3));
}

// ─── Selección y tiradores ────────────────────────────────────────────────

function deselect(): void {
  document.querySelectorAll<HTMLElement>('.hotspot.selected').forEach((h) => {
    h.classList.remove('selected');
    h.querySelectorAll('.resize-handle').forEach((rh) => rh.remove());
  });
  selected = null;
  updatePanel();
}

function select(hotspot: HTMLElement): void {
  deselect();
  hotspot.classList.add('selected');
  const corners: Corner[] = ['tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r'];
  corners.forEach((c) => {
    const handle = document.createElement('div');
    handle.className = `resize-handle rh-${c}`;
    handle.dataset.corner = c;
    hotspot.appendChild(handle);
  });
  selected = hotspot;
  updatePanel();
}

// ─── Activación / desactivación ───────────────────────────────────────────

function setEditMode(on: boolean): void {
  editMode = on;
  document.body.classList.toggle('edit-active', on);
  document.querySelectorAll<HTMLAnchorElement>('.hotspot').forEach((h) => {
    h.classList.toggle('editmode', on);
    if (on) {
      const href = h.getAttribute('href');
      if (href) h.dataset.origHref = href;
      h.removeAttribute('href');
    } else if (h.dataset.origHref) {
      h.setAttribute('href', h.dataset.origHref);
    }
  });

  if (on) {
    if (!panel) panel = buildPanel();
    panel.style.display = 'block';
    attachInteractions();
  } else {
    if (panel) panel.style.display = 'none';
    deselect();
  }
}

// ─── Interacciones de drag ────────────────────────────────────────────────

function attachInteractions(): void {
  // Listeners por hotspot — propiedades onmousedown, así que sobreescribir es seguro.
  document.querySelectorAll<HTMLElement>('.hotspot').forEach((h) => {
    h.onmousedown = (e: MouseEvent) => {
      if (!editMode) return;
      const target = e.target as HTMLElement | null;
      if (target?.classList.contains('resize-handle')) return;
      e.preventDefault();
      e.stopPropagation();
      select(h);
      dragging = {
        type: 'move',
        el: h,
        startRect: getRect(h),
        startMouse: { x: e.clientX, y: e.clientY },
      };
    };
  });

  // Listeners globales — sólo se enganchan UNA VEZ.
  if (docListenersAttached) return;
  docListenersAttached = true;

  document.addEventListener('mousedown', (e: MouseEvent) => {
    if (!editMode) return;
    const t = e.target as HTMLElement | null;
    if (!t?.classList.contains('resize-handle')) return;
    const h = t.parentElement as HTMLElement | null;
    if (!h) return;
    e.preventDefault();
    e.stopPropagation();
    const corner = t.dataset.corner as Corner | undefined;
    if (!corner) return;
    select(h);
    const rect = getRect(h);
    dragging = {
      type: 'resize',
      el: h,
      corner,
      startRect: rect,
      startMouse: { x: e.clientX, y: e.clientY },
      ratio: rect.width / rect.height,
    };
  });

  document.addEventListener('mousemove', (e: MouseEvent) => {
    if (!dragging) return;
    const wrap = document.querySelector<HTMLElement>('.scene-wrapper');
    if (!wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const sx = SCENE_W / wrapRect.width;
    const sy = SCENE_H / wrapRect.height;
    const dx = (e.clientX - dragging.startMouse.x) * sx;
    const dy = (e.clientY - dragging.startMouse.y) * sy;

    if (dragging.type === 'move') {
      setRect(dragging.el, {
        left: dragging.startRect.left + dx,
        top: dragging.startRect.top + dy,
        width: dragging.startRect.width,
        height: dragging.startRect.height,
      });
      return;
    }

    // Resize
    const r = dragging.startRect;
    const free = e.shiftKey;
    const ratio = dragging.ratio ?? 1;
    let newL = r.left;
    let newT = r.top;
    let newW = r.width;
    let newH = r.height;

    switch (dragging.corner) {
      // Esquinas (proporcional a menos que Shift)
      case 'br':
        newW = r.width + dx;
        newH = free ? r.height + dy : newW / ratio;
        break;
      case 'tr':
        newW = r.width + dx;
        newH = free ? r.height - dy : newW / ratio;
        newT = r.top + r.height - newH;
        break;
      case 'bl':
        newW = r.width - dx;
        newH = free ? r.height + dy : newW / ratio;
        newL = r.left + r.width - newW;
        break;
      case 'tl':
        newW = r.width - dx;
        newH = free ? r.height - dy : newW / ratio;
        newL = r.left + r.width - newW;
        newT = r.top + r.height - newH;
        break;
      // Bordes (un solo eje, sin proporción)
      case 'r':
        newW = r.width + dx;
        break;
      case 'l':
        newW = r.width - dx;
        newL = r.left + r.width - newW;
        break;
      case 'b':
        newH = r.height + dy;
        break;
      case 't':
        newH = r.height - dy;
        newT = r.top + r.height - newH;
        break;
    }

    if (newW < MIN_SIZE || newH < MIN_SIZE) return;
    setRect(dragging.el, { left: newL, top: newT, width: newW, height: newH });
  });

  document.addEventListener('mouseup', () => {
    dragging = null;
  });
}

// ─── Atajos de teclado ────────────────────────────────────────────────────

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
}

function handleKeydown(e: KeyboardEvent): void {
  // Tecla E: toggle del modo edición. Funciona en cualquier momento, salvo
  // que el modal de la radio esté abierto (en cuyo caso E pertenece a la
  // radio para activar su propio editor).
  if (e.key === 'e' || e.key === 'E') {
    if (isTypingTarget(e.target)) return;
    if (document.getElementById('r-overlay')?.classList.contains('r-open')) return;
    snapshotInitial();
    setEditMode(!editMode);
    e.preventDefault();
    return;
  }
  if (!editMode) return;

  if (e.key === 'h' || e.key === 'H') {
    if (panel) panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
    return;
  }
  if (e.key === 'r' || e.key === 'R') {
    resetAll();
    return;
  }
  if (e.key === 'c' || e.key === 'C') {
    void copyCSS();
    return;
  }
  if (!selected) return;

  const r = getRect(selected);
  const step = e.shiftKey ? 10 : 1;

  // Alt + flechas: redimensionar desde borde derecho/inferior
  if (e.altKey) {
    if (e.key === 'ArrowRight') {
      setRect(selected, { ...r, width: Math.max(MIN_SIZE, r.width + step) });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft') {
      setRect(selected, { ...r, width: Math.max(MIN_SIZE, r.width - step) });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      setRect(selected, { ...r, height: Math.max(MIN_SIZE, r.height + step) });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowUp') {
      setRect(selected, { ...r, height: Math.max(MIN_SIZE, r.height - step) });
      e.preventDefault();
      return;
    }
  }

  // Ctrl/Cmd + flechas: redimensionar desde borde izquierdo/superior
  if (e.ctrlKey || e.metaKey) {
    if (e.key === 'ArrowLeft') {
      setRect(selected, {
        left: r.left - step,
        top: r.top,
        width: Math.max(MIN_SIZE, r.width + step),
        height: r.height,
      });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowRight') {
      setRect(selected, {
        left: r.left + step,
        top: r.top,
        width: Math.max(MIN_SIZE, r.width - step),
        height: r.height,
      });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowUp') {
      setRect(selected, {
        left: r.left,
        top: r.top - step,
        width: r.width,
        height: Math.max(MIN_SIZE, r.height + step),
      });
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      setRect(selected, {
        left: r.left,
        top: r.top + step,
        width: r.width,
        height: Math.max(MIN_SIZE, r.height - step),
      });
      e.preventDefault();
      return;
    }
  }

  // Flechas solas: mover
  if (e.key === 'ArrowLeft') {
    setRect(selected, { ...r, left: r.left - step });
    e.preventDefault();
  } else if (e.key === 'ArrowRight') {
    setRect(selected, { ...r, left: r.left + step });
    e.preventDefault();
  } else if (e.key === 'ArrowUp') {
    setRect(selected, { ...r, top: r.top - step });
    e.preventDefault();
  } else if (e.key === 'ArrowDown') {
    setRect(selected, { ...r, top: r.top + step });
    e.preventDefault();
  }

  // Q / W: escalar manteniendo proporción y centro aproximado
  if (e.key === 'q' || e.key === 'Q') {
    const ratio = r.width / r.height;
    const newW = r.width - 2;
    const newH = newW / ratio;
    setRect(selected, { left: r.left + 1, top: r.top + 1, width: newW, height: newH });
  }
  if (e.key === 'w' || e.key === 'W') {
    const ratio = r.width / r.height;
    const newW = r.width + 2;
    const newH = newW / ratio;
    setRect(selected, { left: r.left - 1, top: r.top - 1, width: newW, height: newH });
  }
}

// ─── Acciones (export, reset) ─────────────────────────────────────────────

async function copyCSS(): Promise<void> {
  const lines: string[] = ['/* Pega esto reemplazando los estilos de los hotspots */', ''];
  document.querySelectorAll<HTMLElement>('.hotspot').forEach((h) => {
    const r = getRect(h);
    lines.push(`/* ${nameOf(h)} */`);
    lines.push(`left:   ${((100 * r.left) / SCENE_W).toFixed(4)}%;`);
    lines.push(`top:    ${((100 * r.top) / SCENE_H).toFixed(4)}%;`);
    lines.push(`width:  ${((100 * r.width) / SCENE_W).toFixed(4)}%;`);
    lines.push(`height: ${((100 * r.height) / SCENE_H).toFixed(4)}%;`);
    lines.push('');
  });
  const txt = lines.join('\n');
  try {
    await navigator.clipboard.writeText(txt);
    flashNotice('CSS copiado al portapapeles ✓');
  } catch {
    console.log(txt);
    flashNotice('Copia falló — ver consola (F12)');
  }
}

async function exportJSON(): Promise<void> {
  const out: Record<string, [number, number, number, number]> = {};
  document.querySelectorAll<HTMLElement>('.hotspot').forEach((h) => {
    const r = getRect(h);
    out[nameOf(h)] = [
      +((100 * r.left) / SCENE_W).toFixed(4),
      +((100 * r.top) / SCENE_H).toFixed(4),
      +((100 * r.width) / SCENE_W).toFixed(4),
      +((100 * r.height) / SCENE_H).toFixed(4),
    ];
  });
  const json = JSON.stringify(out, null, 2);
  try {
    await navigator.clipboard.writeText(json);
    flashNotice('JSON copiado al portapapeles ✓');
  } catch {
    console.log(json);
    flashNotice('JSON en consola (F12)');
  }
}

function resetAll(): void {
  if (!initialState) return;
  document.querySelectorAll<HTMLElement>('.hotspot').forEach((h) => {
    const css = initialState!.get(nameOf(h));
    if (css !== undefined) h.style.cssText = css;
  });
  deselect();
  flashNotice('Reset ✓');
}

// ─── Notificación flotante ────────────────────────────────────────────────

let noticeTimeout: number | null = null;

function flashNotice(text: string): void {
  let n = document.getElementById('ep-notice');
  if (!n) {
    n = document.createElement('div');
    n.id = 'ep-notice';
    document.body.appendChild(n);
  }
  n.textContent = text;
  n.style.opacity = '1';
  n.style.transition = '';
  if (noticeTimeout !== null) window.clearTimeout(noticeTimeout);
  noticeTimeout = window.setTimeout(() => {
    if (!n) return;
    n.style.transition = 'opacity .5s';
    n.style.opacity = '0';
  }, 1800);
}

// ─── Hint de bienvenida ───────────────────────────────────────────────────

/**
 * Muestra un hint flotante "Pulsa E para entrar en modo EDICIÓN" durante unos
 * segundos al cargar. Se puede deshabilitar con `showHint: false` en initEditor.
 */
function showWelcomeHint(): void {
  const hint = document.createElement('div');
  hint.className = 'editor-hint';
  hint.innerHTML = '🎯 Pulsa <strong style="color:#39ff14">E</strong> para entrar en modo EDICIÓN';
  document.body.appendChild(hint);
  window.setTimeout(() => {
    hint.style.transition = 'opacity 1s';
    hint.style.opacity = '0';
    window.setTimeout(() => hint.remove(), 1100);
  }, 4000);
}

// ─── API pública ──────────────────────────────────────────────────────────

export interface EditorOptions {
  /** Mostrar el hint de bienvenida al cargar (true por defecto). */
  showHint?: boolean;
}

/**
 * Inicializa el editor de hotspots. Idempotente: llamar varias veces no
 * duplica listeners.
 */
export function initEditor(options: EditorOptions = {}): void {
  if (initialized) return;
  initialized = true;

  document.addEventListener('keydown', handleKeydown);

  if (options.showHint !== false) {
    window.setTimeout(showWelcomeHint, 500);
  }
}

/** Devuelve si el editor está activo en este momento. */
export function isEditMode(): boolean {
  return editMode;
}

/** Activa/desactiva el modo edición programáticamente. */
export function toggleEditMode(on?: boolean): void {
  snapshotInitial();
  setEditMode(on ?? !editMode);
}
